import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { darkTheme } from '../../theme/tokens';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { entityPlural } from '../catalog/entityTerms';
import { defaultChatPlaceholder } from './chatPlaceholders';
import { CHAT_FOCUS_EVENT, consumePendingChatFocus } from './chatFocus';

const t = darkTheme;
const MAX_HEIGHT = 160;

export interface ChatInputProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({
  onSend,
  disabled = false,
  placeholder,
}: ChatInputProps) {
  const { active } = useActiveCatalog();
  const resolvedPlaceholder = placeholder ?? defaultChatPlaceholder(entityPlural(active));
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const trimmed = value.trim();
  const canSend = !disabled && trimmed.length > 0;

  // Auto-grow the textarea up to MAX_HEIGHT. Resetting height to 'auto' first
  // lets the textarea shrink back when content is deleted; scrollHeight then
  // reflects the natural content height. Past the cap, overflow scrolls.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, [value]);

  // Cross-surface "put me in the composer" seam (SC-2873) — the View Library's
  // ask-the-agent CTA fires this; see chatFocus.ts. The mount-time consume
  // covers a request fired while the chat panel was collapsed (no composer
  // mounted to hear the event — it mounts one commit later).
  useEffect(() => {
    if (consumePendingChatFocus()) textareaRef.current?.focus();
    const onFocusRequest = () => {
      consumePendingChatFocus();
      textareaRef.current?.focus();
    };
    window.addEventListener(CHAT_FOCUS_EVENT, onFocusRequest);
    return () => window.removeEventListener(CHAT_FOCUS_EVENT, onFocusRequest);
  }, []);

  const handleSend = () => {
    if (!canSend) return;
    onSend(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter inserts a newline. Standard chat affordance.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      data-testid="chat-input-wrapper"
      data-focused={focused ? 'true' : 'false'}
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 8,
        background: t.surface,
        border: `1px solid ${focused ? t.blue : t.border}`,
        borderRadius: 12,
        padding: '10px 12px',
        // Focus ring lives on the wrapper because the textarea has no visible
        // border of its own — keyboard users need *some* cue when focus lands.
        boxShadow: focused ? `0 0 0 2px ${t.blueBg}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
    >
      <textarea
        ref={textareaRef}
        data-testid="chat-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        rows={1}
        style={{
          flex: 1,
          minHeight: 24,
          maxHeight: MAX_HEIGHT,
          overflowY: 'auto',
          resize: 'none',
          border: 'none',
          // outline: 'none' is intentional — focus indication moves to the
          // wrapper div (see boxShadow above). Don't restore the default
          // browser outline; it'd render *inside* our pill border and look broken.
          outline: 'none',
          background: 'transparent',
          color: t.text,
          fontSize: 14,
          fontFamily: 'inherit',
          lineHeight: 1.5,
          padding: '4px 2px',
        }}
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={!canSend}
        aria-label="Send message"
        data-testid="chat-send"
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: canSend ? t.blue : t.surfaceAlt,
          color: canSend ? '#FFF' : t.text3,
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: canSend ? 'pointer' : 'not-allowed',
          fontSize: 16,
          flexShrink: 0,
          transition: 'background 0.15s',
        }}
      >
        →
      </button>
    </div>
  );
}
